import { useState } from 'react'
import { ChevronDown, Settings, LogOut, Bot } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ThemeToggle'

interface Agent {
  agent_id: string
  name: string
  description?: string
  agent_type?: string
}

interface UserInfo {
  username: string
}

interface ChatHeaderProps {
  agents: Agent[]
  selectedAgent: Agent | null
  onAgentSelect: (agent: Agent) => void
  user: UserInfo | null
  onLogout: () => void
  onManageAgents: () => void
}

/**
 * Slim 48px header for the chat UI.
 * Logo, agent selector, theme toggle, user avatar.
 */
export function ChatHeader({
  agents,
  selectedAgent,
  onAgentSelect,
  user,
  onLogout,
  onManageAgents,
}: ChatHeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleAgentSelect = (agent: Agent) => {
    onAgentSelect(agent)
    setIsDropdownOpen(false)
  }

  return (
    <header className="h-12 border-b border-border bg-card/80 backdrop-blur-sm flex-shrink-0 relative z-50">
      <div className="h-full px-4 flex items-center justify-between">
        {/* Left: Brand + Agent Selector */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground text-sm hidden sm:block">
              Universal AI Platform
            </span>
          </div>

          {/* Agent Selector Dropdown */}
          {selectedAgent && agents.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/50 border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <span>{selectedAgent.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>

              {isDropdownOpen && (
                <>
                  {/* Backdrop — inside same stacking context so dropdown items stay clickable */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsDropdownOpen(false)}
                  />

                  {/* Dropdown menu */}
                  <div className="absolute top-full left-0 mt-1.5 w-64 bg-popover border border-border rounded-lg shadow-lg z-50 py-1">
                    {agents.map((agent) => (
                      <button
                        key={agent.agent_id}
                        onClick={() => handleAgentSelect(agent)}
                        className={`w-full px-3 py-2 text-left hover:bg-accent transition-colors ${
                          agent.agent_id === selectedAgent.agent_id
                            ? 'bg-accent/50 font-medium'
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium">{agent.name}</span>
                          {agent.agent_type === 'tool' && (
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                              Tool
                            </span>
                          )}
                          {agent.agent_type === 'sql' && (
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              SQL
                            </span>
                          )}
                          {agent.agent_type === 'generic' && (
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              RAG
                            </span>
                          )}
                        </div>
                        {agent.description && (
                          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {agent.description}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Single agent name display */}
          {selectedAgent && agents.length <= 1 && (
            <span className="text-sm text-muted-foreground">{selectedAgent.name}</span>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onManageAgents} className="h-8 px-2">
            <Settings className="h-4 w-4" />
          </Button>
          <ThemeToggle />
          {user && (
            <div className="flex items-center gap-1.5 ml-1">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                {user.username.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={onLogout} className="h-8 px-2">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  )
}
